import React, { useContext, useEffect, useState } from "react";
import "../../Tabels/Tabels.css";
import { useForm } from "react-hook-form";
import { Card, Container, Row, Col, Form } from "react-bootstrap";
import { Toaster } from "react-hot-toast";

import Header from "../../Header/Header";
import ModalSave from "../../../common/ModalSave";
import { SaveButton } from "../../../common/Button";
import { Context } from "../../../../utils/context";
import { Permission } from "../../../../utils/permissions";

const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10 MB

// Returns an error message when the chosen file is not an acceptable PDF
const validatePdf = (files) => {
  const file = files?.[0];
  if (!file) return true;

  const isPdf =
    file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
  if (!isPdf) return "Document must be a PDF file";
  if (file.size > MAX_DOCUMENT_SIZE) return "Document must be 10 MB or smaller";
  return true;
};

// The licence is a single record: it can be viewed and updated, never added
// or deleted.
const Tables = () => {
  const { getData, postData, isAllow, IMG_URL } = useContext(Context);
  const canEdit = isAllow?.includes(Permission.Licence.Edit.name);

  const [licenceDocument, setLicenceDocument] = useState(null);
  const [showModal, setShowModal] = useState({ code: 0, message: "" });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const loadLicence = async () => {
    const response = await getData("/information/licence");
    const data = response?.data;

    reset({
      company_name: data?.company_name || "",
      licence_no: data?.licence_no || "",
      issuing_authority: data?.issuing_authority || "",
      validity: data?.validity || "",
      document: undefined,
    });
    setLicenceDocument(data?.document || null);
  };

  useEffect(() => {
    loadLicence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append("company_name", data.company_name);
      formData.append("licence_no", data.licence_no);
      formData.append("issuing_authority", data.issuing_authority);
      formData.append("validity", data.validity);
      // Only send a document when a new one was chosen
      if (data.document?.[0]) {
        formData.append("document", data.document[0]);
      }

      const response = await postData("/information/licence", formData);

      setShowModal({ code: response?.code, message: response?.message });
      setTimeout(() => setShowModal({ code: 0, message: "" }), 1500);

      if (response?.success) {
        loadLicence();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const textField = (name, label, placeholder) => (
    <Row className="mt-4">
      <Form.Label column sm={3}>
        {label}
      </Form.Label>
      <Col sm={6}>
        <Form.Control
          type="text"
          placeholder={placeholder}
          disabled={!canEdit}
          isInvalid={!!errors[name]}
          {...register(name, { required: `${label} is required` })}
        />
        {errors[name] && (
          <span className="text-danger">{errors[name].message}</span>
        )}
      </Col>
    </Row>
  );

  return (
    <>
      <div className="main-advancedashboard">
        <Header title={"Licence"} link={"/information/licence"} />
        <section className="AdvanceDashboard">
          <div className="col-xxl-12 col-xl-12">
            <Card className="shadow-lg p-3 mb-5 mt-3 bg-white rounded">
              <Card.Body>
                <Card.Title>Licence</Card.Title>
                <hr />

                <Container>
                  <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Row>
                      <Col lg={8} md={10} className="mx-auto Add-content">
                        {textField("company_name", "Company Name", "Company Name")}
                        {textField("licence_no", "Licence No", "Licence No")}
                        {textField(
                          "issuing_authority",
                          "Issuing Authority",
                          "Issuing Authority",
                        )}

                        {/* Validity */}
                        <Row className="mt-4">
                          <Form.Label column sm={3}>
                            Validity
                          </Form.Label>
                          <Col sm={6}>
                            <Form.Control
                              type="date"
                              disabled={!canEdit}
                              isInvalid={!!errors.validity}
                              {...register("validity", {
                                required: "Validity is required",
                              })}
                            />
                            {errors.validity && (
                              <span className="text-danger">
                                {errors.validity.message}
                              </span>
                            )}
                          </Col>
                        </Row>

                        {/* Document */}
                        <Row className="mt-4">
                          <Form.Label column sm={3}>
                            Document (PDF)
                          </Form.Label>
                          <Col sm={6}>
                            {canEdit && (
                              <Form.Control
                                type="file"
                                accept="application/pdf,.pdf"
                                isInvalid={!!errors.document}
                                {...register("document", {
                                  // a document is needed until one is uploaded
                                  validate: (files) =>
                                    files?.[0] || licenceDocument
                                      ? validatePdf(files)
                                      : "Document is required",
                                })}
                              />
                            )}
                            {errors.document && (
                              <span className="text-danger">
                                {errors.document.message}
                              </span>
                            )}
                            {licenceDocument && (
                              <div className="mt-2">
                                <a
                                  href={IMG_URL + licenceDocument}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View current document
                                </a>
                              </div>
                            )}
                          </Col>
                        </Row>

                        {canEdit && (
                          <Row className="mt-5 pb-3">
                            <div className="d-flex justify-content-center">
                              <SaveButton name="save" type="submit" />
                            </div>
                          </Row>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </Container>
              </Card.Body>
            </Card>
          </div>
        </section>
      </div>

      <ModalSave
        message={showModal.message}
        showErrorModal={!!showModal.code}
      />
      <Toaster position="top-right" />
    </>
  );
};

export default Tables;
